import { ProcessState } from './process';
import { ErrorState } from './error';

export interface ErrorSection {
  section: string;
  id: string;
  errors: ErrorWithDescription[];
}

export interface ErrorWithDescription {
  name: string;
  description: string;
}

type ProcessLabels = { [X in keyof ProcessState]?: string };

export const umbrellaLabels: ProcessLabels = {
  contact: '担当者',
  title: 'タイトル',
  shortDescription: '概要',
  longDescription: '説明',
  domains: '業務分野',
  kle: 'KLE番号',
  kla: 'KLの業務手順バンク',
  klId: 'KL ID'
};

export const umbrellaKeys: Array<keyof ProcessLabels> = Object.keys(umbrellaLabels) as Array<keyof ProcessLabels>;

export const generalInformationLabels: ProcessLabels = {
  phase: 'フェーズ',
  owner: '業務担当者',
  contact: '担当者',
  status: 'ステータス',
  statusText: 'ステータスの説明',
  title: 'タイトル',
  shortDescription: '概要',
  domains: '業務分野',
  visibility: '公開範囲',
  legalClause: '根拠法令・条項',
  kle: 'KLE番号',
  kla: 'KLの業務手順バンク',
  vendor: 'ベンダー',
  klId: 'KL ID',
  orgUnits: '部署',
  sepMep: 'SEP/MEP'
};

export const generalInformationKeys: Array<keyof ProcessLabels> = Object.keys(generalInformationLabels) as Array<
  keyof ProcessLabels
>;

export const challengesLabels: ProcessLabels = {
  longDescription: '説明',
  processChallenges: '現行プロセスの課題',
  solutionRequests: '解決策の説明',
  itSystems: '現行システム'
};

export const challengeKeys: Array<keyof ProcessLabels> = Object.keys(challengesLabels) as Array<keyof ProcessLabels>;

export const timeAndProcessLabels: ProcessLabels = {
  timeSpendOccurancesPerEmployee: '年間のプロセス実施回数',
  timeSpendPerOccurance: '1回あたりの所要時間（分）',
  timeSpendEmployeesDoingProcess: 'プロセスを実施する職員数',
  timeSpendPercentageDigital: '時間削減率（%）',
  timeSpendComputedTotal: '総削減時間',
  timeSpendComment: '所要時間に関するコメント',
  expectedDevelopmentTime: '開発の想定工数（時間）'
};

export const timeAndProcessKeys: Array<keyof ProcessLabels> = Object.keys(timeAndProcessLabels) as Array<
  keyof ProcessLabels
>;

export const assessmentLabels: ProcessLabels = {
  levelOfProfessionalAssessment: 'プロセスにどの程度、専門的な判断が含まれますか？',
  levelOfChange: 'プロセスはどの程度、頻繁な変更の影響を受けますか？',
  levelOfStructuredInformation: 'プロセスはどの程度、構造化された情報に基づいていますか？',
  levelOfUniformity: 'プロセスの処理方法にばらつきはありますか？',
  levelOfDigitalInformation:
    'プロセスで使用するデータや情報は、ITシステム上でデジタルに利用できますか？',
  levelOfQuality:
    '自動化により、より均一でミスの少ない、高い品質が実現しますか？',
  levelOfSpeed: '自動化により、より迅速で充実したサービスが実現しますか？',
  levelOfRoutineWorkReduction:
    '自動化により時間が生まれ、定型業務が減ることで、職員の働きやすさが向上しますか？',
  evaluatedLevelOfRoi: 'プロセスはどの程度自動化できると評価されますか？'
};

export const assessmentKeys: Array<keyof ProcessLabels> = Object.keys(assessmentLabels) as Array<keyof ProcessLabels>;

export const attachmentsLabels: ProcessLabels = {
  esdhReference: '文書管理システム（ESDH）の案件番号'
};

export const attachmentKeys: Array<keyof ProcessLabels> = Object.keys(attachmentsLabels) as Array<keyof ProcessLabels>;

export const implementationLabels: ProcessLabels = {
  technologies: '使用技術',
  runPeriod: '実行頻度',
  technicalImplementationNotes: '技術面の導入',
  organizationalImplementationNotes: '組織面の導入',
  services: '自動化で使用するシステム／インターフェース'
};

export const implementationKeys: Array<keyof ProcessLabels> = Object.keys(implementationLabels) as Array<
  keyof ProcessLabels
>;

export const operationLabels: ProcessLabels = {
  rating: 'ソリューションは期待した効果をどの程度達成しましたか？',
  ratingComment: '実現した効果に関するコメント',
  putIntoOperation: '運用開始日',
  decommissioned: '運用終了日',
  legalClauseLastVerified: '根拠法令の最終確認日'
};

export const operationKeys: Array<keyof ProcessLabels> = Object.keys(operationLabels) as Array<keyof ProcessLabels>;

export const processLabels: ProcessLabels = {
  ...generalInformationLabels,
  ...challengesLabels,
  ...timeAndProcessLabels,
  ...assessmentLabels,
  ...attachmentsLabels,
  ...implementationLabels,
  ...operationLabels,

  internalNotes: '内部メモ'
};

export const processKeys: Array<keyof ProcessLabels> = Object.keys(processLabels) as Array<keyof ProcessLabels>;

export interface ErrorLabels {
  generalInformation: Array<keyof ProcessState>;
  challenges: Array<keyof ProcessState>;
  assessment: Array<keyof ProcessState>;
  timeAndProcess: Array<keyof ProcessState>;
  attachments: Array<keyof ProcessState>;
  implementation: Array<keyof ProcessState>;
  operation: Array<keyof ProcessState>;
}

export const errorLimitations: { [key: string]: string } = {
  klId: '64文字以内',
  title: '1～65文字',
  visibility: '必須項目',
  status: '必須項目',
  kla: '8・11・14文字のいずれか',
  timeSpendOccurancesPerEmployee: '数値のみ',
  timeSpendPerOccurance: '数値のみ',
  expectedDevelopmentTime: '数値のみ',
  timeSpendEmployeesDoingProcess: '数値のみ',
  timeSpendPercentageDigital: '100%以下',
  technologies: '仕様策定フェーズ以降は必須',
  runPeriod: '仕様策定フェーズ以降は必須',
  processChallenges: 'アイデアフェーズ以降は必須'
};

export const errorLabels: ErrorLabels = {
  generalInformation: Object.keys(generalInformationLabels) as Array<keyof ProcessState>,
  challenges: Object.keys(challengesLabels) as Array<keyof ProcessState>,
  assessment: Object.keys(assessmentLabels) as Array<keyof ProcessState>,
  timeAndProcess: Object.keys(timeAndProcessLabels) as Array<keyof ProcessState>,
  attachments: Object.keys(attachmentsLabels) as Array<keyof ProcessState>,
  implementation: Object.keys(implementationLabels) as Array<keyof ProcessState>,
  operation: Object.keys(operationLabels) as Array<keyof ProcessState>
};

export const defaultLabelKeys: Array<keyof ErrorLabels> = Object.keys(errorLabels) as Array<keyof ErrorLabels>;

export const defaultErrorState: ErrorState = {
  generalInformation: {
    section: '基本情報',
    id: 'general-information',
    errors: []
  },
  challenges: {
    section: '課題',
    id: 'challenges',
    errors: []
  },
  assessment: {
    section: '業務面の評価',
    id: 'assessment',
    errors: []
  },
  timeAndProcess: {
    section: '時間とプロセス',
    id: 'time-and-process',
    errors: []
  },
  attachments: {
    section: '添付ファイルとリンク',
    id: 'attachments',
    errors: []
  },
  implementation: {
    section: '開発と導入',
    id: 'implementation',
    errors: []
  },
  operation: {
    section: '運用',
    id: 'operation',
    errors: []
  }
};

export const defaultSectionKeys: Array<keyof ErrorState> = Object.keys(defaultErrorState) as Array<keyof ErrorState>;
